import React from 'react'
import ReactDOM from 'react-dom/client'
import { RouterProvider } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { router } from '@/routes'
import { useAuthStore } from '@/store/authStore'
import { useUIStore } from '@/store/uiStore'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import '@/index.css'

// Create QueryClient with sensible defaults
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error: unknown) => {
        const status = (error as { statusCode?: number })?.statusCode
        // Don't retry on 4xx errors
        if (status && status >= 400 && status < 500) return false
        return failureCount < 2
      },
      staleTime: 1000 * 30,      // 30 seconds — data refreshes quickly after any mutation
      refetchOnWindowFocus: true, // re-fetch when user switches back to the browser tab
    },
    mutations: {
      retry: false,
    },
  },
})

// Initialize app - load theme and auth state before rendering
async function initApp() {
  // Apply theme from localStorage immediately (before React renders)
  const { loadTheme } = useUIStore.getState()
  loadTheme()

  // Try to restore auth session
  const { loadFromStorage } = useAuthStore.getState()
  await loadFromStorage()
}

// Start initialization then render
initApp().then(() => {
  const root = document.getElementById('root')
  if (!root) throw new Error('Root element not found')

  ReactDOM.createRoot(root).render(
    <React.StrictMode>
      <ErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router} />
          {import.meta.env.DEV && (
            <ReactQueryDevtools initialIsOpen={false} position="bottom" />
          )}
        </QueryClientProvider>
      </ErrorBoundary>
    </React.StrictMode>
  )
})
